import "jszip";
import * as ko from "knockout";
import * as _ from "lodash";
import * as UI from "../common/ui";
import { IApplication } from "../interfaces/application";
import * as Constants from "./constants";
import * as Global from "./global";
import * as Util from "./util";

// ----------------------------------------------
// gridFilter control
// ----------------------------------------------
interface IGridFilterOption {
    name: string;
    checked: boolean;
}

interface IGridFilterOptionObservable {
    name: KnockoutObservable<string>;
    checked: KnockoutObservable<boolean>;
}

interface IGridFilterButton {
    name: string;

    iconClass?: string;

    onClick: () => void;
}

interface IGridFilterUIWidgetSettings {
    grid: kendo.ui.Grid;
    showTextSearch?: boolean;
    showRefresh?: boolean;
    showFilters?: boolean;
    filters?: IGridFilterOption[];
    showExportToExcel?: boolean;
    buttons?: IGridFilterButton[];
    showSearchAllFields?: boolean;
}

export interface IGridFilterValues {
    searchText?: string;
    searchAllFields?: boolean;
    filters?: IGridFilterOption[];
}

interface IGridFilterBase {
    SearchText?: string;
    IncludeActive: boolean;
    IncludeInactive: boolean;
    SearchAllFields?: boolean;
}

export const GRIDFILTERCONTROL_HEIGHT: number = 96;

export class GridFilterControl {
    // private properties
    private viewModel: any = {};
    private settings: IGridFilterUIWidgetSettings;
    private container: JQuery;
    private searchTimeoutID: number;

    // constructors
    constructor(container: JQuery, settings: IGridFilterUIWidgetSettings) {
        const me: GridFilterControl = this;
        const app: IApplication = Global.app();

        this.settings = settings;
        this.container = container;

        app.templates().render("gridfiltercontrol", "template_gridfiltercontrol", this.container)
            .done(() => {
                me.viewModel = {
                    showTextSearch: settings.showTextSearch != null ? settings.showTextSearch : false,
                    showRefresh: settings.showRefresh != null ? settings.showRefresh : false,
                    showFilters: settings.showFilters != null ? settings.showFilters : false,
                    filters: me.createFilterObservableArray(settings.filters ? settings.filters : []),
                    showExportToExcel: settings.showExportToExcel != null ? settings.showExportToExcel : false,
                    buttons: settings.buttons != null ? settings.buttons : [],
                    searchText: ko.observable(""),
                    showSearchAllFields: ko.observable(settings.showSearchAllFields),
                    searchAllFields: ko.observable(false),
                    search(): void {
                        me.refreshGrid();
                    },
                    filterChange(): boolean {
                        me.refreshGrid();
                        // let event propagate so menu closes
                        return true;
                    },
                    refresh(): void {
                        me.refreshGrid();
                    },
                    exportClick(): void {
                        if (me.settings.grid != null) {
                            me.settings.grid.saveAsExcel();
                        }
                    },
                };

                // bind the view model
                ko.applyBindings(me.viewModel, container[0]);

                // create controls
                me.container.find("ul[data-id='filterMenu']").kendoMenu();
                me.container.find("ul[data-id='buttons']").kendoMenu();

                // bind events
                // trap enter key and do search
                me.container.find("input[data-id='filterSearchText']").on("keydown", (event: any) => {
                    return me.searchKeyDown(event);
                });
            });
    }

    public getValues(): IGridFilterValues {
        const filterValues: IGridFilterValues = {};

        if (this.viewModel.searchText != null) {
            filterValues.searchText = this.viewModel.searchText();
        }

        if (this.viewModel.filters != null) {
            filterValues.filters = ko.toJS(this.viewModel.filters);
        }

        if (this.viewModel.searchAllFields != null) {
            filterValues.searchAllFields = this.viewModel.searchAllFields();
        }

        return filterValues;
    }

    public setDataFilter(data: IGridFilterBase): void {
        let values: IGridFilterValues;

        data.IncludeActive = true;
        data.IncludeInactive = false;

        values = this.getValues();
        data.SearchText = values.searchText;

        if (values.filters) {
            values.filters.forEach((filter: IGridFilterOption) => {
                switch (filter.name) {
                    case Constants.PHRASE_FILTER_SHOWACTIVE:
                        data.IncludeActive = filter.checked;
                        break;
                    case Constants.PHRASE_FILTER_SHOWINACTIVE:
                        data.IncludeInactive = filter.checked;
                        break;
                }
            });
        }
    }

    // private methods
    private searchKeyDown(event: any): boolean {
        const me: GridFilterControl = this;
        const keyCode: number = event.keyCode ? event.keyCode : event.which ? event.which : event.charCode;

        if (keyCode === 13) {
            me.refreshGrid();
            return false;
        } else {
            if (me.searchTimeoutID != null) {
                window.clearTimeout(me.searchTimeoutID);
            }
            // wait 1/2 second before refreshing the tree.  This basically will wait
            // until the user is done typing before refreshing
            me.searchTimeoutID = window.setTimeout(
                () => {
                    me.refreshGrid();
                },
                500);
            return true;
        }
    }

    private createFilterObservableArray(filters: IGridFilterOption[]): KnockoutObservableArray<IGridFilterOptionObservable> {
        const filterObservable: KnockoutObservableArray<IGridFilterOptionObservable> = ko.observableArray<IGridFilterOptionObservable>([]);

        if (filters != null) {
            filters.forEach((filter: IGridFilterOption) => {
                filterObservable.push({
                    name: ko.observable(filter.name),
                    checked: ko.observable(filter.checked),
                });
            });
        }

        return filterObservable;
    }

    private refreshGrid(): void {
        if (this.settings.grid != null) {
            this.settings.grid.dataSource.read();
            this.settings.grid.dataSource.page(1);
        }
    }
}

// ----------------------------------------------
// functions for configuration a grid using
// dynamic columns.
// ----------------------------------------------
export interface IResultScheme {
    /// the name of the ID field
    ID: string;

    /// definition for all columns returned in the DataSourceResult
    Columns: IResultSchemeColumn[];
}

export interface IResultSchemeColumn {
    /// the column name that is displayed at the top of the grid
    Title: string;

    /// the name of the fields in the data structure
    Field: string;

    /// the data type of the field (string, number, date, boolean)
    Type: string;

    /// if true, make this value a link that opens up the details page
    IsLinkToDetail: boolean;

    /// if true, this value is displayed in the grid
    ShowInGrid: boolean;

    /// Additional formatting information.
    /// Currently used for Date and DateTime formatting.
    Format: string;

    // template HTML to use for rendering the column
    TemplateHTML?: string;
}

export const RESULT_SCHEME_COLUMN_TYPE_STRING = "string";
export const RESULT_SCHEME_COLUMN_TYPE_NUMBER = "number";
export const RESULT_SCHEME_COLUMN_TYPE_BOOLEAN = "boolean";
export const RESULT_SCHEME_COLUMN_TYPE_DATE = "date";

export const RESULT_SCHEME_COLUMN_FORMAT_DATE = "date";
export const RESULT_SCHEME_COLUMN_FORMAT_DATE_TIME = "date-time";
export const RESULT_SCHEME_COLUMN_FORMAT_TIME = "time";

// creates the data structure that can be passed in as the scheme.mode
// when creating a kendo data source, for use by a grid
export function createDataSourceModel(resultScheme: IResultScheme): any {
    const model: any = {
        id: resultScheme.ID,
        fields: {},
    };

    resultScheme.Columns.forEach((column: IResultSchemeColumn) => {
        column.Title = _.escape(column.Title);
        model.fields[column.Field] = {
            type: column.Type,
        };
    });

    return model;
}

// creates the data structure that can be used for the columns property
// when creating a kendo grid
export function createColumnsModel(resultScheme: IResultScheme): kendo.ui.GridColumn[] {
    const gridColumns: kendo.ui.GridColumn[] = [];
    const app = Global.app();

    const customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
    const dateFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
    const timeFormat = UI.GetCustomerSetting(customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);

    for (const column of resultScheme.Columns) {
        if (column.ShowInGrid) {
            const gridColumn: kendo.ui.GridColumn = {
                title: column.Title,
                field: column.Field,
            };
            if (!Util.isNullOrEmpty(column.TemplateHTML)) {
                gridColumn.template = kendo.template(column.TemplateHTML ? column.TemplateHTML : "");
            } else {
                switch (column.Format) {
                    case RESULT_SCHEME_COLUMN_FORMAT_DATE:
                        gridColumn.template =
                            `#= ${column.Field} ? kendo.toString(${column.Field}, "${UI.getKendoDateFormat(dateFormat)}") : ""#`;
                        break;

                    case RESULT_SCHEME_COLUMN_FORMAT_DATE_TIME:
                        gridColumn.template =
                            // tslint:disable-next-line: max-line-length
                            `#= ${column.Field} ? kendo.toString(${column.Field}, "${UI.getKendoDateFormat(dateFormat)} ${UI.getKendoTimeFormat(timeFormat)}") : ""#`;
                        break;

                    case RESULT_SCHEME_COLUMN_FORMAT_TIME:
                        gridColumn.template =
                            `#= ${column.Field} ? kendo.toString(new Date("2000-01-01T" + ${column.Field}), "${UI.getKendoTimeFormat(timeFormat)}") : ""#`;
                        gridColumn.filterable = false;
                        break;
                }
                if (column.Type === RESULT_SCHEME_COLUMN_TYPE_DATE) {
                    gridColumn.filterable =  {
                        ui: (element: any): any => {
                            element.kendoDatePicker({
                                format: UI.getKendoDateFormat(dateFormat),
                            });
                        },
                    };
                }
            }
            gridColumns.push(gridColumn);
        }
    }

    return gridColumns;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/gridFilter.ts